import React, { FC, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import MuiButton from "@mui/material/Button";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import Select from "@/components/Select";
import { useAppTheme } from "@/utils/theme";
import { useUserStore } from "@/hooks/useUserStore";
import { useAvailableLanguagesSetting } from "@/hooks/useSetting";
import {
  CATEGORY_TYPES,
  Category,
  CategoryPayload,
  CategoryType,
} from "@/domain/category.interface";
import categoryValidationSchema, {
  CategoryFormData,
} from "./validation.schema";
import CategoryItemFields from "./CategoryItemFields";

type CategoryModalProps = {
  open: boolean;
  onClose: () => void;
  category?: Category;
  onSave: (data: CategoryPayload) => Promise<void>;
  isPending: boolean;
};

const EMPTY_LANGUAGE = { language: "", name: "", description: "" };

const EMPTY_ITEM = {
  icon: "",
  hexaColor: "",
  textColor: "",
  status: "ACTIVE" as const,
  languages: [EMPTY_LANGUAGE],
};

const DEFAULT_VALUES: CategoryFormData = {
  type: "EDUCATIONAL_RESOURCE",
  status: "ACTIVE",
  phoneNumber: "",
  categories: [EMPTY_ITEM],
};

const CategoryModal: FC<CategoryModalProps> = ({
  open,
  onClose,
  category,
  onSave,
  isPending,
}) => {
  const { t } = useTranslation("common");
  const theme = useAppTheme();
  const { userInfo } = useUserStore();
  const isEdit = !!category;

  const availableLanguages = useAvailableLanguagesSetting();
  const languageOptions = useMemo(
    () => availableLanguages.map((code) => ({ key: code, value: code })),
    [availableLanguages]
  );

  const typeOptions = useMemo(
    () => CATEGORY_TYPES.map((type) => ({ key: type, value: type })),
    []
  );

  const methods = useForm<CategoryFormData>({
    resolver: yupResolver(categoryValidationSchema(t)),
    mode: "all",
    defaultValues: DEFAULT_VALUES,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "categories",
  });

  useEffect(() => {
    if (category) {
      reset({
        type: (category.type as CategoryType) || "EDUCATIONAL_RESOURCE",
        status: (category.status as "ACTIVE" | "INACTIVE") || "ACTIVE",
        phoneNumber: category.phoneNumber || "",
        categories:
          category.categories && category.categories.length > 0
            ? category.categories.map((item) => ({
                _id: item._id,
                icon: item.icon || "",
                hexaColor: item.hexaColor || "",
                textColor: item.textColor || "",
                status: (item.status as "ACTIVE" | "INACTIVE") || "ACTIVE",
                languages:
                  item.languages && item.languages.length > 0
                    ? item.languages.map((l) => ({
                        language: l.language,
                        name: l.name,
                        description: l.description || "",
                      }))
                    : [EMPTY_LANGUAGE],
              }))
            : [EMPTY_ITEM],
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [category, reset]);

  const onSubmit = async (data: CategoryFormData) => {
    const currentUserId = userInfo?._id as string;
    const payload: CategoryPayload = {
      type: data.type as CategoryType,
      status: data.status as CategoryPayload["status"],
      ...(data.phoneNumber ? { phoneNumber: data.phoneNumber.trim() } : {}),
      categories: (data.categories || []).map((item) => ({
        ...(item._id ? { _id: item._id } : {}),
        ...(item.icon ? { icon: item.icon.trim() } : {}),
        ...(item.hexaColor ? { hexaColor: item.hexaColor.trim() } : {}),
        ...(item.textColor ? { textColor: item.textColor.trim() } : {}),
        status: item.status as "ACTIVE" | "INACTIVE",
        languages: (item.languages || []).map((l) => ({
          language: l.language,
          name: (l.name || "").trim(),
          ...(l.description ? { description: l.description.trim() } : {}),
        })),
      })),
      ...(isEdit ? { updatedBy: currentUserId } : { createdBy: currentUserId }),
    };
    await onSave(payload);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      title={isEdit ? "Edit Category" : "New category"}
      theme={theme}
      size={{
        width: "100%",
        minHeight: 640,
        maxHeight: 780,
        maxWidth: 920,
      }}
      content={
        <FormProvider {...methods}>
          <Box sx={{ width: "100%", pt: "12px" }}>
            <Grid container spacing={"20px"}>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={typeOptions}
                      value={field.value as string}
                      label="Type"
                      id="category-type"
                      required
                      error={!!errors.type}
                      errorMessage={errors.type?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={[
                        { key: "ACTIVE", value: "Active" },
                        { key: "INACTIVE", value: "Inactive" },
                      ]}
                      value={field.value as string}
                      label="Status"
                      id="category-status"
                      error={!!errors.status}
                      errorMessage={errors.status?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller
                  name="phoneNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="category-phone"
                      label="Phone number"
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="titleSmall">Items *</Typography>
                  <MuiButton
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => append(EMPTY_ITEM)}
                  >
                    Add item
                  </MuiButton>
                </Box>
                {typeof errors.categories?.message === "string" && (
                  <Typography
                    variant="bodySmall"
                    sx={{ color: theme.palette.error.main, mb: 1 }}
                  >
                    {errors.categories.message}
                  </Typography>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {fields.map((itemField, index) => (
                    <CategoryItemFields
                      key={itemField.id}
                      itemIndex={index}
                      onRemoveItem={() => remove(index)}
                      canRemoveItem={fields.length > 1}
                      languageOptions={languageOptions}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        </FormProvider>
      }
      actions={
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default CategoryModal;
